
import { useState, useCallback, useEffect } from 'react'
import { apiCall } from '@/lib/api/api-helpers'

import type { StudioMediaFolder as CanvasFolder, StudioMediaFile as CanvasFile } from '@genieartinc/schemas/graph'
export type { StudioMediaFolder as CanvasFolder, StudioMediaFile as CanvasFile } from '@genieartinc/schemas/graph'

interface UseCanvasFilesOptions {
  canvasId: string | null
}

export function useCanvasFiles({ canvasId }: UseCanvasFilesOptions) {
  const [folders, setFolders] = useState<CanvasFolder[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [files, setFiles] = useState<CanvasFile[]>([])
  const [filesLoading, setFilesLoading] = useState(false)

  const fetchFolders = useCallback(async () => {
    if (!canvasId) return
    setLoading(true)
    setError(null)
    try {
      const json = await apiCall(`/api/canvas/${canvasId}/folders`, true) as { success: boolean; data: CanvasFolder[]; error?: string }
      if (json.success) {
        setFolders(json.data)
      } else {
        setError(json.error ?? 'Failed to load folders')
      }
    } catch (err) {
      setError('Failed to load folders')
    } finally {
      setLoading(false)
    }
  }, [canvasId])

  const fetchFiles = useCallback(async () => {
    if (!canvasId) return
    setFilesLoading(true)
    try {
      const json = await apiCall(`/api/canvas/${canvasId}/files?status=ACTIVE`, true) as { success: boolean; data: CanvasFile[] }
      if (json.success) {
        setFiles(json.data as CanvasFile[])
      }
    } catch {
      // silently ignore — files list is non-critical
    } finally {
      setFilesLoading(false)
    }
  }, [canvasId])

  useEffect(() => {
    fetchFolders()
    fetchFiles()
  }, [fetchFolders, fetchFiles])

  useEffect(() => {
    const handler = () => { void fetchFiles(); void fetchFolders() }
    window.addEventListener('genie:file-created', handler)
    return () => window.removeEventListener('genie:file-created', handler)
  }, [fetchFiles, fetchFolders])

  const createFolder = useCallback(async (name: string): Promise<CanvasFolder | null> => {
    if (!canvasId) return null
    try {
      const json = await apiCall(`/api/canvas/${canvasId}/folders`, true, {
        method: 'POST',
        body: JSON.stringify({ name }),
      }) as { success: boolean; data: CanvasFolder }
      if (json.success) {
        setFolders((prev) => [...prev, json.data])
        return json.data
      }
      console.error('[useCanvasFiles] createFolder failed:', json)
      return null
    } catch (err) {
      console.error('[useCanvasFiles] createFolder error:', err)
      return null
    }
  }, [canvasId])

  const renameFolder = useCallback(async (folderId: string, name: string): Promise<boolean> => {
    if (!canvasId) return false
    try {
      const json = await apiCall(`/api/canvas/${canvasId}/folders/${folderId}`, true, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      }) as { success: boolean; data: { name: string } }
      if (json.success) {
        setFolders((prev) => prev.map((f) => (f.id === folderId ? { ...f, name: json.data.name } : f)))
        return true
      }
      return false
    } catch {
      return false
    }
  }, [canvasId])

  const deleteFolder = useCallback(async (folderId: string): Promise<boolean> => {
    if (!canvasId) return false
    try {
      const json = await apiCall(`/api/canvas/${canvasId}/folders/${folderId}`, true, {
        method: 'DELETE',
      }) as { success: boolean }
      if (json.success) {
        setFolders((prev) => prev.filter((f) => f.id !== folderId))
        return true
      }
      return false
    } catch {
      return false
    }
  }, [canvasId])

  const duplicateFolder = useCallback(async (folderId: string): Promise<CanvasFolder | null> => {
    if (!canvasId) return null
    try {
      const json = await apiCall(`/api/canvas/${canvasId}/folders/${folderId}/duplicate`, true, {
        method: 'POST',
      }) as { success: boolean; data: CanvasFolder }
      if (json.success) {
        setFolders((prev) => [...prev, json.data])
        return json.data
      }
      return null
    } catch {
      return null
    }
  }, [canvasId])

  const moveFile = useCallback(async (fileUuid: string, folderId: string): Promise<boolean> => {
    if (!canvasId) return false
    try {
      const json = await apiCall(`/api/canvas/${canvasId}/files/${fileUuid}`, true, {
        method: 'PUT',
        body: JSON.stringify({ folderId }),
      }) as { success: boolean }
      if (json.success) {
        // Remove file from root list (it now belongs to a folder)
        setFiles((prev) => prev.filter((f) => f.uuid !== fileUuid))
        // Increment the target folder's fileCount
        setFolders((prev) =>
          prev.map((folder) =>
            folder.id === folderId ? { ...folder, fileCount: folder.fileCount + 1 } : folder
          )
        )
        return true
      }
      return false
    } catch {
      return false
    }
  }, [canvasId])

  const uploadFile = useCallback(async (file: File, nodeId?: string): Promise<CanvasFile | null> => {
    if (!canvasId) return null
    const formData = new FormData()
    formData.append('file', file)
    if (nodeId) formData.append('nodeId', nodeId)
    try {
      // Raw fetch for multipart/form-data — apiCall sets Content-Type: application/json which breaks FormData boundary
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL
      if (!apiBaseUrl) throw new Error('VITE_API_BASE_URL is not configured')
      const res = await fetch(`${apiBaseUrl}/api/canvas/${canvasId}/files`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })
      const json = await res.json()
      if (json.success) {
        const newFile = json.data as CanvasFile
        setFiles((prev) => [newFile, ...prev])
        window.dispatchEvent(new Event('genie:file-created'))
        return newFile
      }
      return null
    } catch {
      return null
    }
  }, [canvasId])

  const deleteFile = useCallback(async (fileUuid: string): Promise<boolean> => {
    if (!canvasId) return false
    try {
      const json = await apiCall(`/api/canvas/${canvasId}/files/${fileUuid}`, true, {
        method: 'DELETE',
      }) as { success: boolean }
      if (json.success) {
        setFiles((prev) => prev.filter((f) => f.uuid !== fileUuid))
        return true
      }
      return false
    } catch {
      return false
    }
  }, [canvasId])

  return {
    folders,
    loading,
    error,
    createFolder,
    renameFolder,
    deleteFolder,
    duplicateFolder,
    refetch: fetchFolders,
    files,
    filesLoading,
    fetchFiles,
    moveFile,
    uploadFile,
    deleteFile,
  }
}
